import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import blackLoginPicture from '../../assets/blackLoginPicture.webp';
import whiteLoginPicture from '../../assets/whiteLoginPicture.webp';
import { useTheme } from '../../ThemeContext';
import { Modal, Button, CloseButton, Form } from 'react-bootstrap';
import { Check2Square, Square, ArrowRight } from 'react-bootstrap-icons'
import EmailDeliveryHintAlert from '../ui/EmailDeliveryHintAlert';
import { PUBLIC_URL } from '../../config/endpoints';

const HANDSHAKE_STORAGE_KEY = 'salew:handshakeId';
const REF_CLAIM_STORAGE_KEY = 'salew:pendingRefClaimToken';
const TELEGRAM_LINKS_STORAGE_KEY = 'salew:telegramFallbackLinks';
const TELEGRAM_LINKS_TTL_MS = 5 * 60 * 1000;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const SUPPORT_TELEGRAM_URL = 'https://t.me/SaleW_Support';
const SUPPORT_MAX_URL = 'https://max.ru/u/f9LHodD0cOLkybkMaACuMPTooxWoTrXIaccAneeGAfvHKvf7aKrFOq6HAvA';

const LoginPage = () => {
  const { theme } = useTheme();
  const backgroundImage = theme === 'dark' ? blackLoginPicture : whiteLoginPicture;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    startBotHandshake,
    checkBotHandshakeStatus,
    finalizeBotHandshake,
    sendEmailAuthCode,
    verifyEmailAuthCode,
    showModal,
    setShowModal,
  } = useAuth();

  const [isChecked, setIsChecked] = useState(false);
  const initialHandshakeId = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return window.localStorage.getItem(HANDSHAKE_STORAGE_KEY);
    } catch (error) {
      console.warn('Не удалось прочитать данные авторизации из localStorage:', error);
      return null;
    }
  }, []);
  const [handshakeId, setHandshakeId] = useState(initialHandshakeId);
  const [handshakeStatus, setHandshakeStatus] = useState(initialHandshakeId ? 'pending' : null);
  const [isProcessing, setIsProcessing] = useState(Boolean(initialHandshakeId));
  const [preparedHandshakeId, setPreparedHandshakeId] = useState(null);
  const [isPreparingHandshake, setIsPreparingHandshake] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState('email');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailInfo, setEmailInfo] = useState('');
  const [showEmailAuthModal, setShowEmailAuthModal] = useState(false);
  const [showEmailDeliveryHint, setShowEmailDeliveryHint] = useState(false);
  const [telegramFallbackDeepUrl, setTelegramFallbackDeepUrl] = useState('');
  const fallbackTimerRef = useRef(null);
  const [pendingRefClaimToken, setPendingRefClaimToken] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(REF_CLAIM_STORAGE_KEY);
    if (!raw) return null;
    const normalized = String(raw).trim();
    return normalized || null;
  });
  const finalizingRef = useRef(false);
  const generateHandshakeId = useCallback(() => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID().replace(/-/g, '');
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.slice(0, 48);
  }, []);

  const clearHandshakeState = useCallback((status = null) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(HANDSHAKE_STORAGE_KEY);
        window.localStorage.removeItem(TELEGRAM_LINKS_STORAGE_KEY);
      } catch (error) {
        console.warn('Не удалось очистить данные авторизации из localStorage:', error);
      }
    }
    finalizingRef.current = false;
    setHandshakeId(null);
    setHandshakeStatus(status);
    setIsProcessing(false);
    setPreparedHandshakeId(null);
    setIsPreparingHandshake(false);
    setTelegramFallbackDeepUrl('');
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const scheduleTelegramFallbackCleanup = useCallback((ttlMs) => {
    if (typeof window === 'undefined') {
      return;
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    fallbackTimerRef.current = window.setTimeout(() => {
      setTelegramFallbackDeepUrl('');
      try {
        window.localStorage.removeItem(TELEGRAM_LINKS_STORAGE_KEY);
      } catch (error) {
        console.warn('Не удалось очистить Telegram fallback ссылки:', error);
      }
      fallbackTimerRef.current = null;
    }, ttlMs);
  }, []);

  const storeTelegramFallbackLinks = useCallback((telegramDeepUrl) => {
    if (typeof window === 'undefined') {
      return;
    }
    setTelegramFallbackDeepUrl(telegramDeepUrl);
    try {
      window.localStorage.setItem(
        TELEGRAM_LINKS_STORAGE_KEY,
        JSON.stringify({
          telegramDeepUrl,
          expiresAt: Date.now() + TELEGRAM_LINKS_TTL_MS,
        })
      );
    } catch (error) {
      console.warn('Не удалось сохранить Telegram fallback ссылки:', error);
    }
    scheduleTelegramFallbackCleanup(TELEGRAM_LINKS_TTL_MS);
  }, [scheduleTelegramFallbackCleanup]);

  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(TELEGRAM_LINKS_STORAGE_KEY);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      const restoredDeepUrl = typeof parsed?.telegramDeepUrl === 'string' ? parsed.telegramDeepUrl : '';
      const expiresAt = Number(parsed?.expiresAt);
      const remainingMs = expiresAt - Date.now();

      if (!restoredDeepUrl || !Number.isFinite(expiresAt) || remainingMs <= 0) {
        window.localStorage.removeItem(TELEGRAM_LINKS_STORAGE_KEY);
        return;
      }

      setTelegramFallbackDeepUrl(restoredDeepUrl);
      scheduleTelegramFallbackCleanup(remainingMs);
    } catch (error) {
      console.warn('Не удалось восстановить Telegram fallback ссылки:', error);
      try {
        window.localStorage.removeItem(TELEGRAM_LINKS_STORAGE_KEY);
      } catch (removeError) {
        console.warn('Не удалось удалить поврежденные Telegram fallback ссылки:', removeError);
      }
    }
  }, [scheduleTelegramFallbackCleanup]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const claimFromQuery = (params.get('ref_claim') || '').trim();
    if (!claimFromQuery) {
      return;
    }

    setPendingRefClaimToken(claimFromQuery);
    try {
      window.localStorage.setItem(REF_CLAIM_STORAGE_KEY, claimFromQuery);
    } catch (error) {
      console.warn('Не удалось сохранить ref claim token в localStorage:', error);
    }
  }, [location.search]);

  useEffect(() => {
    // If referral context changes, previously prepared handshake can become stale.
    setPreparedHandshakeId(null);
  }, [pendingRefClaimToken]);

  const prepareBotHandshake = useCallback(async () => {
    if (
      !isChecked ||
      isProcessing ||
      handshakeId ||
      preparedHandshakeId ||
      isPreparingHandshake
    ) {
      return;
    }

    try {
      setIsPreparingHandshake(true);
      setErrorMessage('');
      const newHandshakeId = generateHandshakeId();
      await startBotHandshake(newHandshakeId, {
        platform: navigator?.platform || 'unknown',
        ref_claim_token: pendingRefClaimToken || undefined,
      });
      setPreparedHandshakeId(newHandshakeId);
    } catch (error) {
      console.error('Ошибка подготовки авторизации через бота:', error);
      setErrorMessage(error.message || 'Не удалось подготовить авторизацию через Telegram-бота');
    } finally {
      setIsPreparingHandshake(false);
    }
  }, [
    isChecked,
    isProcessing,
    handshakeId,
    preparedHandshakeId,
    isPreparingHandshake,
    generateHandshakeId,
    startBotHandshake,
    pendingRefClaimToken,
  ]);

  useEffect(() => {
    if (
      !isChecked ||
      isProcessing ||
      handshakeId ||
      preparedHandshakeId ||
      isPreparingHandshake ||
      errorMessage
    ) {
      return;
    }
    void prepareBotHandshake();
  }, [
    isChecked,
    isProcessing,
    handshakeId,
    preparedHandshakeId,
    isPreparingHandshake,
    errorMessage,
    prepareBotHandshake,
  ]);

  const handleOpenBot = useCallback(() => {
    if (!isChecked || isProcessing || isPreparingHandshake) {
      return;
    }

    if (!preparedHandshakeId) {
      void prepareBotHandshake();
      return;
    }

    try {
      setIsProcessing(true);
      setHandshakeStatus('pending');
      setErrorMessage('');
      finalizingRef.current = false;

      const newHandshakeId = preparedHandshakeId;
      const telegramDeepUrl = `tg://resolve?domain=salew_bot&start=auth_${newHandshakeId}`;

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(HANDSHAKE_STORAGE_KEY, newHandshakeId);
        } catch (error) {
          console.warn('Не удалось сохранить данные авторизации в localStorage:', error);
        }
      }

      setHandshakeId(newHandshakeId);
      setPreparedHandshakeId(null);
      setHandshakeStatus('pending');
      storeTelegramFallbackLinks(telegramDeepUrl);

      window.location.assign(telegramDeepUrl);
    } catch (error) {
      console.error('Ошибка запуска авторизации через бота:', error);
      setErrorMessage(error.message || 'Не удалось начать авторизацию через Telegram-бота');
      setIsProcessing(false);
    }
  }, [
    isChecked,
    isProcessing,
    isPreparingHandshake,
    preparedHandshakeId,
    prepareBotHandshake,
    storeTelegramFallbackLinks,
  ]);

  const handleOpenEmailModal = useCallback(() => {
    if (!isChecked || isProcessing) {
      return;
    }
    setEmailStep('email');
    setEmailCode('');
    setEmailError('');
    setEmailInfo('');
    setShowEmailAuthModal(true);
  }, [isChecked, isProcessing]);

  const handleSendEmailCode = useCallback(async () => {
    if (!isChecked || emailLoading) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailError('Введите корректный email');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailInfo('');

    try {
      const response = await sendEmailAuthCode(normalizedEmail);
      setEmail(normalizedEmail);
      setEmailStep('code');
      setEmailInfo(
        response?.detail ||
        'Код отправлен на email. Проверьте входящие и папку Спам.'
      );
      setShowEmailDeliveryHint(true);
    } catch (error) {
      setEmailError(error.message || 'Не удалось отправить код');
    } finally {
      setEmailLoading(false);
    }
  }, [email, isChecked, emailLoading, sendEmailAuthCode]);

  const handleVerifyEmailCode = useCallback(async () => {
    if (!isChecked || emailLoading) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedCode = emailCode.trim();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailError('Введите корректный email');
      return;
    }
    if (!/^\d{6}$/.test(normalizedCode)) {
      setEmailError('Код должен состоять из 6 цифр');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailInfo('');

    try {
      const response = await verifyEmailAuthCode(
        normalizedEmail,
        normalizedCode,
        pendingRefClaimToken
      );
      if (response?.token) {
        try {
          window.localStorage.removeItem(REF_CLAIM_STORAGE_KEY);
        } catch (error) {
          console.warn('Не удалось очистить сохраненного реферала:', error);
        }
        setPendingRefClaimToken(null);
        setShowEmailAuthModal(false);
        navigate('/');
        return;
      }
      setEmailError(response?.detail || 'Не удалось завершить вход');
    } catch (error) {
      setEmailError(error.message || 'Не удалось подтвердить код');
    } finally {
      setEmailLoading(false);
    }
  }, [
    email,
    emailCode,
    isChecked,
    emailLoading,
    verifyEmailAuthCode,
    pendingRefClaimToken,
    navigate,
  ]);

  useEffect(() => {
    if (!handshakeId) {
      return;
    }

    let active = true;
    let timerId;

    const pollStatus = async () => {
      if (!active) {
        return;
      }

      try {
        const statusData = await checkBotHandshakeStatus(handshakeId);
        const status = statusData?.status;

        if (status) {
          setHandshakeStatus(status);
        }

        if (status === 'confirmed') {
          if (!finalizingRef.current) {
            finalizingRef.current = true;
            try {
              const finalizeData = await finalizeBotHandshake(handshakeId);
              if (finalizeData?.token) {
                try {
                  window.localStorage.removeItem(REF_CLAIM_STORAGE_KEY);
                } catch (error) {
                  console.warn('Не удалось очистить сохраненного реферала:', error);
                }
                setPendingRefClaimToken(null);
                active = false;
                clearHandshakeState('completed');
                setErrorMessage('');
                navigate('/');
                return;
              }

              if (finalizeData?.detail === 'handshake_pending') {
                finalizingRef.current = false;
              } else if (finalizeData?.detail) {
                setErrorMessage(finalizeData.detail);
              }
            } catch (finalizeError) {
              finalizingRef.current = false;
              console.error('Ошибка завершения авторизации через бота:', finalizeError);
              setErrorMessage(finalizeError.message || 'Не удалось завершить авторизацию через Telegram-бота');
            }
          }
        } else if (status === 'completed') {
          active = false;
          clearHandshakeState('completed');
          setErrorMessage('');
        } else if (status === 'expired' || status === 'not_found') {
          active = false;
          clearHandshakeState(status);
          setErrorMessage('Ссылка устарела или не найдена. Попробуйте запустить авторизацию заново.');
        } else if (status) {
          setErrorMessage('');
        }
      } catch (statusError) {
        console.error('Ошибка проверки статуса авторизации через бота:', statusError);
        setErrorMessage(statusError.message || 'Не удалось проверить статус авторизации через бота');
      } finally {
        if (active) {
          timerId = window.setTimeout(pollStatus, 5000);
        }
      }
    };

    pollStatus();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        pollStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      active = false;
      if (timerId) {
        clearTimeout(timerId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      finalizingRef.current = false;
    };
  }, [handshakeId, checkBotHandshakeStatus, finalizeBotHandshake, clearHandshakeState, navigate]);

  const statusText = useMemo(() => {
    switch (handshakeStatus) {
      case 'pending':
        return 'Ожидаем подтверждение в Telegram. После завершения вернитесь в приложение.';
      case 'confirmed':
        return 'Подтверждение получено, завершаем авторизацию…';
      case 'completed':
        return 'Авторизация завершена. Открываем приложение…';
      case 'expired':
        return 'Ссылка устарела. Попробуйте начать авторизацию заново.';
      case 'not_found':
        return 'Сессия авторизации не найдена. Попробуйте ещё раз.';
      default:
        return null;
    }
  }, [handshakeStatus]);


  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
    >
      <div
        className="position-absolute w-100 h-100"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
        }}
      />

      <div className="position-relative text-center p-5 rounded-4 shadow-lg"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(33,37,41,0.95)' : 'rgba(255,255,255,0.95)',
          maxWidth: '450px',
          width: '90%',
          border: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6',
        }}>
        <h2 className="mb-4 fw-bold" style={{ color: theme === 'dark' ? '#fff' : '#212529' }}>
          Добро пожаловать
        </h2>

        <div className="d-flex flex-column gap-3 mb-4">
          <button
            onClick={() => setIsChecked(!isChecked)}
            className="d-flex align-items-center gap-2 bg-transparent border-0 p-0"
            style={{ color: theme === 'dark' ? '#fff' : '#212529' }}
          >
            {isChecked ? (
              <Check2Square size={57} className="text-primary" />
            ) : (
              <Square size={50} className="text-secondary" />
            )}
            <span className="text-start small">
              Я согласен с{' '}
              <a
                href={`${PUBLIC_URL}/agreement.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                style={{ color: '#0d6efd' }}
              >
                пользовательским соглашением
              </a>{' '}
              и{' '}
              <a
                href={`${PUBLIC_URL}/politika-obrabotki-personalnyh-dannyh.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                style={{ color: '#0d6efd' }}
              >
                Политикой обработки персональных данных
              </a>
            </span>
          </button>

          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              onClick={handleOpenBot}
              className={`w-100 py-2 fw-bold login-auth-btn ${theme === 'dark' ? 'login-auth-btn--primary-dark' : 'login-auth-btn--primary-light'}`}
              disabled={!isChecked || isProcessing || isPreparingHandshake}
            >
              {isProcessing ? 'Ожидаем подтверждение…' : isPreparingHandshake ? 'Подготавливаем ссылку…' : 'Войти через Telegram'}
            </button>
            <button
              type="button"
              onClick={handleOpenEmailModal}
              className={`w-100 py-2 fw-semibold login-auth-btn ${theme === 'dark' ? 'login-auth-btn--secondary-dark' : 'login-auth-btn--secondary-light'}`}
              disabled={!isChecked || isProcessing}
            >
              Войти по Email
            </button>
          </div>

          {!isChecked && (
            <p
              className="text-start small mb-0"
              style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}
            >
              Чтобы продолжить, примите условия выше.
            </p>
          )}

          {(statusText || errorMessage || telegramFallbackDeepUrl) && (
            <div className="text-start small">
              {statusText && (
                <p className="text-start small mb-0"
                  style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
                  {statusText}
                </p>
              )}
              {telegramFallbackDeepUrl && (
                <p className="mb-1" style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
                  Если Telegram не открылся автоматически, попробуйте открыть вручную:{' '}
                  <a
                    href={telegramFallbackDeepUrl}
                    className="text-decoration-none"
                    style={{ color: '#0d6efd' }}
                  >
                    через tg://
                  </a>
                  . Ссылка активна 5 минут.
                </p>
              )}
              {errorMessage && (
                <p className="mb-0 text-danger">
                  {errorMessage}
                </p>
              )}
            </div>
          )}

          {pendingRefClaimToken && (
            <div className="text-start small">
              {pendingRefClaimToken && (
                <p className="mb-0" style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
                  Реферальная ссылка сохранена и применится после входа.
                </p>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 small">
          <p className="mb-0">
            📹{' '}
            <a
              href={`${PUBLIC_URL}/videos`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none d-inline-flex align-items-center gap-1"
              style={{ color: '#0d6efd' }}
            >
              Видеоинструкция
            </a>
          </p>
        </div>
        <div className="mt-4 small">
          <p className="mb-0">
            Вся история обновлений и модификаций бота @salew_bot и приложения доступна в ТГ канале{' '}
            <a
              href="tg://resolve?domain=salew_app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none d-inline-flex align-items-center gap-1"
              style={{ color: '#0d6efd' }}
            >
              @salew_app <ArrowRight />
            </a>
          </p>
        </div>
      </div>

      <Modal
        show={showEmailAuthModal}
        onHide={() => !emailLoading && setShowEmailAuthModal(false)}
        className={theme === 'dark' ? 'text-white modal-dark' : 'text-dark modal-light'}
        dialogClassName="login-email-auth-modal-dialog"
        centered
      >
        <Modal.Header
          className={theme === 'dark'
            ? 'border-bottom border-secondary bg-dark text-white'
            : 'border-bottom border-light bg-light text-dark'
          }
        >
          <Modal.Title className="fw-bold">Вход по Email</Modal.Title>
          <CloseButton
            variant={theme === 'dark' ? 'white' : 'dark'}
            onClick={() => !emailLoading && setShowEmailAuthModal(false)}
            disabled={emailLoading}
          />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          <div className="d-flex flex-column gap-3">
            <p className="text-start small mb-0" style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
              Если у вас уже есть подписка в боте/приложении, сначала войдите через Telegram и добавьте email в настройках.
            </p>
            <p className="text-start small mb-0" style={{ color: theme === 'dark' ? '#868e96' : '#6c757d' }}>
              Код отправляется на почту и действует ограниченное время.
            </p>

            <Form.Group className="text-start">
              <Form.Label className="small mb-1">Email</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@example.com"
                className="login-email-input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={emailLoading || emailStep === 'code'}
              />
            </Form.Group>

            {emailStep === 'code' && (
              <Form.Group className="text-start">
                <Form.Label className="small mb-1">Код из письма</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 цифр"
                  className="login-email-input"
                  value={emailCode}
                  onChange={(event) => setEmailCode(event.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  disabled={emailLoading}
                />
              </Form.Group>
            )}

            <div className="d-flex gap-2">
              {emailStep === 'email' ? (
                <Button
                  variant="light"
                  className={`w-100 fw-semibold login-modal-btn ${theme === 'dark' ? 'login-modal-btn--primary-dark' : 'login-modal-btn--primary-light'}`}
                  disabled={!isChecked || emailLoading}
                  onClick={handleSendEmailCode}
                >
                  {emailLoading ? 'Отправка…' : 'Отправить код'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="light"
                    className={`fw-semibold login-modal-btn ${theme === 'dark' ? 'login-modal-btn--secondary-dark' : 'login-modal-btn--secondary-light'}`}
                    disabled={emailLoading}
                    onClick={() => {
                      setEmailStep('email');
                      setEmailCode('');
                      setEmailError('');
                    }}
                  >
                    Назад
                  </Button>
                  <Button
                    variant="light"
                    className={`w-100 fw-semibold login-modal-btn ${theme === 'dark' ? 'login-modal-btn--primary-dark' : 'login-modal-btn--primary-light'}`}
                    disabled={!isChecked || emailLoading}
                    onClick={handleVerifyEmailCode}
                  >
                    {emailLoading ? 'Проверка…' : 'Подтвердить код'}
                  </Button>
                </>
              )}
            </div>

            {(emailInfo || emailError) && (
              <div className="text-start small">
                {emailInfo && (
                  <p className="mb-1" style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
                    {emailInfo}
                  </p>
                )}
                {emailError && (
                  <>
                    <p className="mb-2" style={{ color: theme === 'dark' ? '#ff8d96' : '#dc3545' }}>
                      {emailError}
                    </p>
                    <div className="login-email-support-card">
                      <div className="login-email-support-title">Не получается войти?</div>
                      <div className="login-email-support-text">Напишите в поддержку, поможем проверить почту и авторизацию.</div>
                      <div className="login-email-support-actions">
                        <a
                          className={`login-email-support-btn ${theme === 'dark' ? 'login-email-support-btn--dark' : 'login-email-support-btn--light'}`}
                          href={SUPPORT_TELEGRAM_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Telegram
                        </a>
                        <a
                          className={`login-email-support-btn ${theme === 'dark' ? 'login-email-support-btn--dark' : 'login-email-support-btn--light'}`}
                          href={SUPPORT_MAX_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          MAX
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <EmailDeliveryHintAlert
        show={showEmailDeliveryHint}
        isDark={theme === 'dark'}
        onClose={() => setShowEmailDeliveryHint(false)}
      />

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        className={theme === 'dark' ? 'text-white modal-dark' : 'text-dark modal-light'}
        centered
      >
        <Modal.Header
          className={theme === 'dark'
            ? 'border-bottom border-secondary bg-dark text-white'
            : 'border-bottom border-light bg-light text-dark'
          }
        >
          <Modal.Title className="fw-bold">Активация бота</Modal.Title>
          <CloseButton
            variant={theme === 'dark' ? 'white' : 'dark'}
            onClick={() => setShowModal(false)}
          />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          <div className="d-flex flex-column gap-3">
            <p className="mb-0">
              Для начала работы необходимо активировать нашего бота.
              Это займет всего несколько секунд!
            </p>
            <Button
              href="tg://resolve?domain=salew_bot"
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              className="d-flex align-items-center gap-2 justify-content-center fw-bold py-2"
            >
              Перейти к боту
              <ArrowRight size={18} />
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LoginPage;
